import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Zap, Brain, Eye, Lightbulb, BookOpen, Languages } from 'lucide-react';

interface GameCardData {
  name: string;
  slug: string;
  category: string;
  categoryIcon: typeof Brain;
  time: string;
  difficulty: string;
  description: string;
}

const GAMES: GameCardData[] = [
  {
    name: 'Pattern Flow',
    slug: 'brain-games-pattern-flow',
    category: 'Memory',
    categoryIcon: Brain,
    time: '3–5 min',
    difficulty: 'Adaptive',
    description: 'Remember and repeat sequences to boost your memory and attention.',
  },
  {
    name: 'Mini Sudoku',
    slug: 'brain-games-mini-sudoku',
    category: 'Problem Solving',
    categoryIcon: Lightbulb,
    time: '5–10 min',
    difficulty: 'Medium',
    description: 'Solve 4×4 number puzzles to enhance logical thinking.',
  },
  {
    name: 'Match & Go',
    slug: 'brain-games-match-and-go',
    category: 'Attention',
    categoryIcon: Eye,
    time: '3–7 min',
    difficulty: 'Easy',
    description: 'Find the single matching symbol between two cards.',
  },
  {
    name: 'Target Search',
    slug: 'brain-games-target-search',
    category: 'Attention',
    categoryIcon: Eye,
    time: '4–8 min',
    difficulty: 'Medium',
    description: 'Find all occurrences of a specific colored shape in a grid.',
  },
  {
    name: 'Detail Detective',
    slug: 'brain-games-detail-detective',
    category: 'Memory',
    categoryIcon: Brain,
    time: '5–10 min',
    difficulty: 'Adaptive',
    description: 'Read a short story and answer recall questions from memory.',
  },
  {
    name: 'Category Collector',
    slug: 'brain-games-category-collector',
    category: 'Learning',
    categoryIcon: BookOpen,
    time: '3–5 min',
    difficulty: 'Medium',
    description: 'Find and tap all words of a given category before time runs out.',
  },
  {
    name: 'Steady Hands Garden',
    slug: 'brain-games-steady-hands-garden',
    category: 'Attention',
    categoryIcon: Eye,
    time: '3–5 min',
    difficulty: 'Adaptive',
    description: 'Tap good items and avoid penalty items in a garden grid.',
  },
  {
    name: 'Trivia Challenge',
    slug: 'brain-games-trivia-challenge',
    category: 'Learning',
    categoryIcon: BookOpen,
    time: '5–10 min',
    difficulty: 'Adaptive',
    description: 'Test your knowledge across various topics with fun trivia questions.',
  },
  {
    name: 'Word Ladder',
    slug: 'brain-games-word-ladder',
    category: 'Language',
    categoryIcon: Languages,
    time: '2–5 min',
    difficulty: 'Medium',
    description: 'Transform one word into another by changing one letter at a time.',
  },
  {
    name: 'Story Sequence',
    slug: 'brain-games-story-sequence',
    category: 'Language',
    categoryIcon: Languages,
    time: '2–4 min',
    difficulty: 'Easy',
    description: 'Arrange shuffled story cards into the correct chronological order.',
  },
  {
    name: 'Number Neighbors',
    slug: 'brain-games-number-neighbors',
    category: 'Problem Solving',
    categoryIcon: Lightbulb,
    time: '1–3 min',
    difficulty: 'Medium',
    description: 'Find pairs of adjacent numbers that add up to the target sum.',
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  Memory: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Attention: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  'Problem Solving': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Learning: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Language: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  Easy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  Adaptive: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
};

const BrainGamesGrid = () => {
  return (
    <div className="my-10">
      <h2 className="text-2xl font-semibold text-primary mb-2">The Games</h2>
      <p className="text-muted-foreground mb-6">Tap any game to learn how it works, what it trains, and how to play.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {GAMES.map((game) => {
          const CategoryIcon = game.categoryIcon;
          return (
            <Link
              key={game.slug}
              to={`/support/older-adults/${game.slug}`}
              className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
            >
              <Card className="h-full p-5 transition-all duration-200 hover:shadow-md hover:border-primary/30 group-hover:-translate-y-0.5 bg-card">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <CategoryIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-base leading-tight">
                      {game.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      {game.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge variant="secondary" className={`text-xs font-medium ${CATEGORY_COLORS[game.category]}`}>
                        {game.category}
                      </Badge>
                      <Badge variant="secondary" className={`text-xs font-medium ${DIFFICULTY_COLORS[game.difficulty]}`}>
                        {game.difficulty}
                      </Badge>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {game.time}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default BrainGamesGrid;
